import { useStableCallback } from "@prismicio/editor-support/React"
import {
  AnimatedElement,
  AnimatedList,
  Box,
  Button,
  Draggable,
  FieldSet,
  HairlineButton,
  Line,
  type RenderDropIndicatorArgs,
} from "@prismicio/editor-ui"
import {
  type LinkContent,
  RepeatableContent,
} from "@prismicio/types-internal/lib/content"
import { type Link } from "@prismicio/types-internal/lib/customtypes"
import { useState } from "react"

import { useEditorConfig } from "../../../EditorConfig"
import { FieldInRepeatableContextProvider } from "../../../FieldContext"
import { LinkField } from "../LinkField"
import { RepeatableLinkFieldActions as Actions } from "./RepeatableLinkFieldActions"
import { useRepeatableLinkField } from "./useRepeatableLinkField"

interface RepeatableLinkFieldProps {
  id?: string
  field: Link
  content: RepeatableContent | undefined
  onContentChange: (content: RepeatableContent) => void
}

export function RepeatableLinkField(props: RepeatableLinkFieldProps) {
  const { id, field } = props
  const repeatableLink = useRepeatableLinkField(props)
  const [isDragging, setIsDragging] = useState(false)
  const { links } = repeatableLink

  return (
    <FieldInRepeatableContextProvider>
      <FieldSet
        id={id}
        legend={getFieldLabel(field.config)}
        variant="compact"
        selected={isDragging}
      >
        <LinkFieldList
          field={field}
          items={links}
          onItemDelete={repeatableLink.deleteLink}
          onItemChange={repeatableLink.updateLink}
          onItemMove={repeatableLink.moveLink}
          onItemAdd={repeatableLink.addLink}
          onItemReorder={repeatableLink.updateLinks}
          onDragStatusChange={setIsDragging}
        />
        <Button
          startIcon="add"
          color="grey"
          onClick={() => repeatableLink.addLink(links.length)}
          sx={{ marginInline: 4, marginBlock: 4 }}
        >
          Add
        </Button>
      </FieldSet>
    </FieldInRepeatableContextProvider>
  )
}

function getFieldLabel(config: Link["config"]) {
  if (config?.label) return `Multiple • ${config.label}`
  return "Multiple"
}

interface LinkFieldListProps {
  field: Link
  items: LinkContent[]
  onItemChange: (content: LinkContent | undefined, index: number) => void
  onItemDelete: (index: number) => void
  onItemMove: (direction: "up" | "down", index: number) => void
  onItemAdd: (index: number) => void
  onItemReorder: (content: LinkContent[]) => void
  onDragStatusChange: (isDragging: boolean) => void
}

function LinkFieldList(props: LinkFieldListProps) {
  const { field, items, onItemChange, onItemDelete, onItemMove } = props

  const isDraggable = items.length > 1
  const dnd = useRepeatableLinkDnd(props)

  return (
    <AnimatedList
      items={items}
      getItemKey={(item) => item.key}
      ariaLabel="Link list"
      dnd={dnd}
    >
      {(item, index) => (
        <Draggable disabled={!isDraggable}>
          <LinkField
            appearance="compact"
            content={item}
            field={field}
            onContentChange={(content) => onItemChange(content, index)}
            backgroundColor="transparent"
          />
          <Actions
            onDelete={() => onItemDelete(index)}
            isDeleteEnabled={items.length > 1}
            onMoveUp={() => onItemMove("up", index)}
            isMoveUpEnabled={index > 0}
            onMoveDown={() => onItemMove("down", index)}
            isMoveDownEnabled={index < items.length - 1}
          />
        </Draggable>
      )}
    </AnimatedList>
  )
}

interface UseRepeatableLinkDndProps {
  onDragStatusChange: (isDragging: boolean) => void
  onItemReorder: (content: LinkContent[]) => void
  onItemAdd: (index: number) => void
}

function useRepeatableLinkDnd(props: UseRepeatableLinkDndProps) {
  const { onDragStatusChange, onItemReorder, onItemAdd } = props

  const { scrollOffsetTop } = useEditorConfig()
  const stableOnItemAdd = useStableCallback(onItemAdd)

  const renderDropIndicator = (props: RenderDropIndicatorArgs) => (
    <DropIndicator {...props} onItemAdd={stableOnItemAdd} />
  )

  return {
    renderDropIndicator,
    scrollOffsetTop,
    onDragChange: onDragStatusChange,
    onReorder: onItemReorder,
  }
}

type DropIndicatorProps = RenderDropIndicatorArgs & {
  onItemAdd: (index: number) => void
}

function DropIndicator(props: DropIndicatorProps) {
  const { isDropTarget, itemIndex, position, itemCount, onItemAdd } = props

  if (itemIndex === 0 && position === "before") {
    return <LineDropTarget isDropTarget={isDropTarget} position="before" />
  }

  if (itemIndex === itemCount - 1 && position === "after") {
    return <LineDropTarget isDropTarget={isDropTarget} position="after" />
  }

  return (
    <AddBelowButton
      index={itemIndex}
      isDropTarget={isDropTarget}
      onItemAdd={onItemAdd}
    />
  )
}

type AddBelowButtonProps = {
  index: number
  isDropTarget: boolean
  onItemAdd: (index: number) => void
}

function AddBelowButton(props: AddBelowButtonProps) {
  const { index, onItemAdd, isDropTarget } = props
  const [key, setKey] = useState(0)

  function onClick() {
    onItemAdd(index + 1)
    // this will remount the button, preventing a fade-out animation after
    // adding a new item, which made the UX feel weird.
    setKey((current) => current + 1)
  }

  return (
    <HairlineButton
      key={key}
      icon="add"
      size="small"
      variant="rightDashed"
      tooltip={{ content: "Add item below" }}
      onClick={onClick}
      dropMode={isDropTarget}
      sx={{ marginInline: 4 }}
    />
  )
}

type LineDropTargetProps = {
  isDropTarget: boolean
  position: "before" | "after"
}

function LineDropTarget(props: LineDropTargetProps) {
  const { isDropTarget, position } = props

  return (
    <AnimatedElement
      enterDuration={150}
      animation={{ name: "expand-vertical" }}
    >
      {isDropTarget && (
        <Box>
          <Line
            key="line"
            color="purple"
            thickness={2}
            sx={{
              width: "100%",
              marginBottom: position === "before" ? 4 : undefined,
              marginTop: position === "after" ? 4 : undefined,
            }}
          />
        </Box>
      )}
    </AnimatedElement>
  )
}
